import { patientCommunicationPath } from '@/lib/patientCommunication';
import {
  unreadConversationCount,
  type PatientMessageConversation,
} from '@/utils/patientMessagesPath';

export type AssignedPatientEmailInboxRow = {
  thread_id: string;
  patient_id: string;
  patient_first_name: string | null;
  patient_last_name: string | null;
  subject: string | null;
  from_address: string | null;
  last_inbound_at: string | null;
  last_message_at: string | null;
};

export type DashboardKommunikationMessageItem = {
  kind: 'message';
  id: string;
  sortAt: string;
  conversation: PatientMessageConversation;
};

export type DashboardKommunikationEmailItem = {
  kind: 'email';
  id: string;
  sortAt: string;
  threadId: string;
  patientId: string;
  patientFirstName: string;
  patientLastName: string;
  subject: string | null;
  fromAddress: string | null;
};

export type DashboardKommunikationItem =
  | DashboardKommunikationMessageItem
  | DashboardKommunikationEmailItem;

export const DASHBOARD_KOMMUNIKATION_PREVIEW_LIMIT = 5;

function sortTimestampMs(iso: string): number {
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : 0;
}

export function assignedEmailPatientName(row: AssignedPatientEmailInboxRow): string {
  return `${row.patient_first_name ?? ''} ${row.patient_last_name ?? ''}`.trim();
}

export function assignedEmailSortAt(row: AssignedPatientEmailInboxRow): string {
  return row.last_inbound_at ?? row.last_message_at ?? '';
}

export function dashboardKommunikationEmailPath(
  patientId: string,
  threadId: string,
): string {
  return patientCommunicationPath(patientId, { channel: 'email', threadId });
}

export function mergeDashboardKommunikationItems(
  conversations: readonly PatientMessageConversation[],
  emails: readonly AssignedPatientEmailInboxRow[],
  limit = DASHBOARD_KOMMUNIKATION_PREVIEW_LIMIT,
): DashboardKommunikationItem[] {
  const items: DashboardKommunikationItem[] = [];

  for (const conversation of conversations) {
    if (conversation.unread_count <= 0) continue;
    items.push({
      kind: 'message',
      id: `message:${conversation.patient_id}`,
      sortAt: conversation.latest_created_at,
      conversation,
    });
  }

  for (const email of emails) {
    items.push({
      kind: 'email',
      id: `email:${email.thread_id}`,
      sortAt: assignedEmailSortAt(email),
      threadId: email.thread_id,
      patientId: email.patient_id,
      patientFirstName: email.patient_first_name ?? '',
      patientLastName: email.patient_last_name ?? '',
      subject: email.subject,
      fromAddress: email.from_address,
    });
  }

  items.sort((a, b) => {
    const byTime = sortTimestampMs(b.sortAt) - sortTimestampMs(a.sortAt);
    if (byTime !== 0) return byTime;
    return a.id.localeCompare(b.id);
  });

  return items.slice(0, limit);
}

export function isMissingAssignedEmailInboxRpc(error: {
  code?: string | null;
  message?: string | null;
}): boolean {
  const code = error.code ?? '';
  if (code === 'PGRST202' || code === '42883') return true;
  return /list_assigned_patient_email_inbox/i.test(error.message ?? '');
}

export function dashboardKommunikationUnreadCount(
  conversations: readonly PatientMessageConversation[],
  emails: readonly AssignedPatientEmailInboxRow[],
): number {
  return unreadConversationCount(conversations) + emails.length;
}
